import { useEffect, useReducer, useRef, useContext, useMemo } from 'react';
import { UserSessionValuesContext } from 'contexts';
import { getLocalSettingsProxy } from 'utilities';

export const CookieCategory = {
  ESSENTIAL: 'essential',
  PERFORMANCE: 'performance',
  FUNCTIONALITY: 'functionality',
};

export function makeCookieCategoryObject(values) {
  if (Array.isArray(values)) {
    return Object.keys(CookieCategory).reduce((acc, key, index) => {
      acc[CookieCategory[key]] = values[index] ?? true;
      return acc;
    }, {});
  }

  if (typeof values === 'object' && values !== null) {
    return Object.keys(CookieCategory).reduce((acc, key) => {
      acc[CookieCategory[key]] = values[key] ?? true;
      return acc;
    }, {});
  }

  return null;
}

export function areCookieCategoryObjectsEqual(obj1, obj2) {
  // If one of the objects is null, they are not equal
  if (!obj1 || !obj2) return false;

  return Object.keys(CookieCategory).every((key) => obj1[key] === obj2[key]);
}

export const initialState = {
  draft: makeCookieCategoryObject([]),
  committed: null,
  isDirty: false,
};

export function reducer(state, action) {
  switch (action.type) {
    case 'ACCEPT_ALL':
      return {
        ...state,
        committed: makeCookieCategoryObject([true, true, true]),
        isDirty: false,
      };
    case 'REJECT_ALL': {
      return {
        ...state,
        // Essential must always be true
        committed: makeCookieCategoryObject([true, false, false]),
        isDirty: false,
      };
    }
    case 'TOGGLE_CATEGORY':
      return {
        ...state,
        draft: {
          ...state.draft,
          [action.category]: !state.draft[action.category],
        },
        isDirty: true,
      };
    case 'COMMIT':
      return {
        ...state,
        committed: { ...state.draft },
        isDirty: false,
      };
    case 'RESET_DRAFT':
      return {
        ...state,
        draft: initialState.draft,
        isDirty: false,
      };
    default:
      return state;
  }
}

export function isValidConsent(consent) {
  return (
    typeof consent === 'object' &&
    consent !== null &&
    typeof consent.timestamp === 'string' &&
    typeof consent.version === 'number'
  );
}

export function getCookieConsentStorageKey(userId) {
  const defaultKey = 'cookieConsent';
  return typeof userId !== 'undefined' && userId !== null
    ? `${defaultKey}.${userId}`
    : defaultKey;
}

export function getStoredCookieConsent(storage, userId) {
  const storageKey = getCookieConsentStorageKey(userId);

  try {
    const storedConsent = JSON.parse(storage[storageKey]);
    const isStoredConsentValid = isValidConsent(storedConsent);

    return isStoredConsentValid ? storedConsent : null;
  } catch {
    return null;
  }
}

export function useCookieConsent() {
  const storage = getLocalSettingsProxy();
  const prevCommittedRef = useRef(null);
  const { user } = useContext(UserSessionValuesContext);

  const storageKey = useMemo(
    () => getCookieConsentStorageKey(user?.id),
    [user?.id],
  );

  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    (initialState) => {
      const storedConsent = getStoredCookieConsent(storage, user?.id);

      if (storedConsent) {
        const storedCategories = makeCookieCategoryObject(storedConsent);
        prevCommittedRef.current = storedCategories;

        return {
          ...initialState,
          committed: storedCategories,
        };
      }
      return initialState;
    },
  );

  useEffect(
    function saveConsentToStorage() {
      const current = state?.committed;
      const prev = prevCommittedRef.current;

      if (!current) return;

      const hasChanged = !areCookieCategoryObjectsEqual(prev, current);
      if (hasChanged) {
        storage[storageKey] = JSON.stringify({
          ...current,
          timestamp: new Date().toISOString(),
          version: 1,
        });
        prevCommittedRef.current = current;
      }
    },
    [state?.committed, storageKey],
  );

  return {
    state,
    hasConsent: state?.committed !== null,
    acceptAll: () => dispatch({ type: 'ACCEPT_ALL' }),
    rejectAll: () => dispatch({ type: 'REJECT_ALL' }),
    toggleCategory: (category) => {
      // Essential category cannot be toggled, it is always true
      if (category !== CookieCategory.ESSENTIAL) {
        dispatch({ type: 'TOGGLE_CATEGORY', category });
      }
    },
    saveChanges: () => dispatch({ type: 'COMMIT' }),
    resetDraft: () => dispatch({ type: 'RESET_DRAFT' }),
  };
}
